/* eslint-disable no-shadow */

import GoogleService from '../../services/google';
import toast from '../../utils/toasts';

const namespaced = true;

const state = () => ({
  sheetsData: [[]],
  workbook: null,
  sheets: [],
  sheetsDetails: [],
  sheetName: '',
  sheetId: null,
  forceConnect: false,
  isLoading: false,
});

const getters = {
  // NOTE: We might also wanna do our filtering
  //  in the backend to cut down on potentially large ranges
  getFilteredData: (state) => (filters) => {
    if (!state?.sheetsData.length) {
      return [[]];
    } // No Data, so return empty array
    let res;
    filters.forEach((filter) => {
      switch (filter.type) {
        case 'pagination': {
          const start = (filter.page - 1) * filter.limit;
          const end = start + filter.limit;

          res = state.sheetsData
            // Start From
            .slice(filter.offset - 1)
            // Page + Limit Per Page
            .slice(start, end);

          break;
        }

        default: {
          // unsupported filter type
          res = [[]];
          break;
        }
      }
    });

    return res;
  },
};

const mutations = {
  /* eslint-disable no-param-reassign */

  UPDATE_SHEETSDATA(state, payload) {
    const { data, sheetName, sheetId } = payload;

    state.sheetsData = data;
    state.sheetName = state.sheetName || sheetName;
    state.sheetId = sheetId;
  },

  UPDATE_WORKBOOK(state, payload) {
    state.workbook = payload;
    state.sheets = payload?.sheetNames || [];
  },

  UPDATE_SHEETS(state, payload) {
    state.sheets = payload.sheets;
  },
  UPDATE_SHEETS_DETAILS(state, payload) {
    state.sheetsDetails = payload;
  },

  CLEAR_SHEETSDATA(state) {
    state.sheetsData = [[]];
    state.workbook = null;
    state.sheets = [];
    state.sheetName = '';
    state.sheetId = null;
  },

  SET_FORCE_CONNECT(state, value) {
    state.forceConnect = value;
  },

  SET_LOADING(state, value) {
    state.isLoading = value;
  },
};

const actions = {
  updateSheets({ commit }, payload) {
    commit('UPDATE_SHEETS', payload);
  },
  updateSheetsDetails({ commit }, payload) {
    commit('UPDATE_SHEETS_DETAILS', payload);
  },

  async getPublicSheetData({ commit }, payload) {
    const { sheet } = payload;

    commit('SET_LOADING', true);

    const workbook = await GoogleService.getPublicSheetData(payload);
    commit('UPDATE_WORKBOOK', workbook);

    if (workbook?.data?.length) {
      commit('UPDATE_SHEETSDATA', {
        data: workbook.data,
        sheetName: sheet,
      });
    } else if (!workbook) {
      throw new Error('Invalid Public Google Sheet URL');
    } else {
      commit('UPDATE_SHEETSDATA', {
        data: [[]],
        sheetName: sheet,
      });
    }

    commit('SET_LOADING', false);
  },

  async getPrivateSheetData(context, payload) {
    if (context.state.isLoading) return; // Prevent multiple requests
    const {
      workbookId,
      accountId,
      sheetId,
      cache = true, // default for cache
    } = payload;

    // Early return if any required part is undefined
    if (workbookId === undefined || accountId === undefined || sheetId === undefined) {
      // syne sometimes is called before the sheet is defined, and causes loading state to be off
      return;
    }

    // Continue only if all required properties are present
    try {
      const { values, statusCode } = await GoogleService.getSheetData({
        workbookId,
        accountId,
        sheetId,
        cache,
      });

      if (!values) {
        context.commit('UPDATE_SHEETSDATA', {
          sheetId,
          data: [[]],
        });

        // google wraps errors in an object, so we have to throw the error message
        throw new Error(`Server error: ${statusCode}`);
      }

      context.commit('UPDATE_SHEETSDATA', {
        sheetId,
        data: values,
      });
    } catch (error) {
      toast.makeToast({
        heading: 'Google Sheets Error:',
        icon: 'error',
        hideAfter: 5000,
        stack: false,
        text: 'There was an error fetching the data from Google. Please try again later.',
      });

      // eslint-disable-next-line no-console
      console.error('Error fetching private sheet data:', error);
    }
  },

  async getPrivateWorkbookSheets({ commit }, payload) {
    const {
      workbookId,
      accountId,
    } = payload;

    const workbookSheets = await GoogleService.getSheetsFromWorkbook({
      workbookId,
      accountId,
    });

    commit('UPDATE_WORKBOOK', { workbook: workbookSheets });
    return workbookSheets;
  },

  clearSheetData({ commit }) {
    commit('CLEAR_SHEETSDATA');
  },

  setForceConnect({ commit }, value) {
    commit('SET_FORCE_CONNECT', value);
  },
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
